import { c as _c } from "react/compiler-runtime";
import React, { use, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import notFoundNewConsumerApp from 'commons/notFoundNewConsumerApp';
import PDPDesktop from './PDPDesktop';
import PDPMobile from './PDPMobile';
import MobileAndTabletOnly from '../MobileAndTabletOnly';
import DesktopOnly from '../DesktopOnly';
import PDPSEOTags from './PDPSEOTags';
import PDPFooter from './PDPFooter';
import PDPContext from './state/context/PDPContext';
import usePDPLuxAnalytics from './hooks/usePDPLuxAnalytics';
import usePDPCreateContextValue from './hooks/usePDPCreateContextValue';
import PDPPageAnalytics from './PDPPageAnalytics';

/**
 * Options + context creation + Provider + above/below-fold tree.
 * Resolves server options/basicDetails promises via use(); hero paint stays in
 * PDPHeroShell. Zip stock / order ETA must not suspend this region.
 */
const PDPContextRegion = t0 => {
  const $ = _c(41);
  const {
    slugOverride: t1,
    isEditOrder: t2,
    pdpIsModularBYOProduct: t3,
    productOptionsPromise,
    productBasicDetailsPromise,
    productHeroImageFromServer: t4,
    productCylindoHeroImage: t5
  } = t0;
  const slugOverride = t1 === undefined ? null : t1;
  const isEditOrder = t2 === undefined ? false : t2;
  const pdpIsModularBYOProduct = t3 === undefined ? false : t3;
  const productHeroImageFromServer = t4 === undefined ? null : t4;
  const productCylindoHeroImage = t5 === undefined ? null : t5;
  const params = useParams();
  const slug = slugOverride ?? params.slug ?? null;
  const productOptions = productOptionsPromise ? use(productOptionsPromise) : null;
  const productBasicDetails = productBasicDetailsPromise ? use(productBasicDetailsPromise) : null;
  let t6;
  if ($[0] !== isEditOrder || $[1] !== pdpIsModularBYOProduct || $[2] !== productBasicDetails || $[3] !== productOptions || $[4] !== slug) {
    t6 = {
      isEditOrder,
      pdpIsModularBYOProduct,
      slug,
      productOptions,
      productBasicDetails
    };
    $[0] = isEditOrder;
    $[1] = pdpIsModularBYOProduct;
    $[2] = productBasicDetails;
    $[3] = productOptions;
    $[4] = slug;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const pdpContextValue = usePDPCreateContextValue(t6);
  usePDPLuxAnalytics();
  let t7;
  bb0: {
    if (productHeroImageFromServer?.src) {
      t7 = productHeroImageFromServer;
      break bb0;
    }
    const heroUrl = pdpContextValue.basicDetails?.data?.hero_images?.[0]?.img?.url;
    if (!heroUrl) {
      t7 = null;
      break bb0;
    }
    const t8 = pdpContextValue.basicDetails?.data?.name ? String(pdpContextValue.basicDetails.data.name) : "";
    let t9;
    if ($[6] !== heroUrl || $[7] !== t8) {
      t9 = {
        src: heroUrl,
        width: 1600,
        height: 2400,
        alt: t8
      };
      $[6] = heroUrl;
      $[7] = t8;
      $[8] = t9;
    } else {
      t9 = $[8];
    }
    t7 = t9;
  }
  const productHeroImage = t7;
  if (pdpContextValue.basicDetails.isProductNotFound) {
    let t8;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = notFoundNewConsumerApp();
      $[9] = t8;
    } else {
      t8 = $[9];
    }
    return t8;
  }
  let t8;
  if ($[10] !== pdpContextValue.stockAvailability?.data) {
    t8 = pdpContextValue.stockAvailability?.data || {};
    $[10] = pdpContextValue.stockAvailability?.data;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const {
    discontinuedCategoryMsg: t9,
    isDiscontinued: t10,
    isOutOfStock: t11,
    showNotification
  } = t8;
  const discontinuedCategoryMsg = t9 === undefined ? null : t9;
  const isDiscontinued = t10 === undefined ? false : t10;
  const isOutOfStock = t11 === undefined ? false : t11;
  let t12;
  if ($[12] !== isOutOfStock) {
    t12 = <PDPSEOTags isOutOfStock={isOutOfStock} />;
    $[12] = isOutOfStock;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const t13 = isDiscontinued ? discontinuedCategoryMsg : null;
  let t14;
  if ($[14] !== isDiscontinued || $[15] !== isEditOrder || $[16] !== isOutOfStock || $[17] !== pdpIsModularBYOProduct || $[18] !== productCylindoHeroImage || $[19] !== productHeroImage || $[20] !== showNotification || $[21] !== slug || $[22] !== t13) {
    t14 = <MobileAndTabletOnly><PDPMobile slug={slug} isEditOrder={isEditOrder} isOutOfStock={isOutOfStock} showOutOfStockNotification={showNotification} pdpIsModularBYOProduct={pdpIsModularBYOProduct} showDiscontinuedNotification={isDiscontinued} discontinuedCategoryMsg={t13} productHeroImage={productHeroImage} productCylindoHeroImage={productCylindoHeroImage} /></MobileAndTabletOnly>;
    $[14] = isDiscontinued;
    $[15] = isEditOrder;
    $[16] = isOutOfStock;
    $[17] = pdpIsModularBYOProduct;
    $[18] = productCylindoHeroImage;
    $[19] = productHeroImage;
    $[20] = showNotification;
    $[21] = slug;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const t15 = isDiscontinued ? discontinuedCategoryMsg : null;
  let t16;
  if ($[24] !== isDiscontinued || $[25] !== isEditOrder || $[26] !== isOutOfStock || $[27] !== pdpIsModularBYOProduct || $[28] !== productCylindoHeroImage || $[29] !== productHeroImage || $[30] !== showNotification || $[31] !== slug || $[32] !== t15) {
    t16 = <DesktopOnly><PDPDesktop slug={slug} isEditOrder={isEditOrder} isOutOfStock={isOutOfStock} showOutOfStockNotification={showNotification} pdpIsModularBYOProduct={pdpIsModularBYOProduct} showDiscontinuedNotification={isDiscontinued} discontinuedCategoryMsg={t15} productHeroImage={productHeroImage} productCylindoHeroImage={productCylindoHeroImage} /></DesktopOnly>;
    $[24] = isDiscontinued;
    $[25] = isEditOrder;
    $[26] = isOutOfStock;
    $[27] = pdpIsModularBYOProduct;
    $[28] = productCylindoHeroImage;
    $[29] = productHeroImage;
    $[30] = showNotification;
    $[31] = slug;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  let t18;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <PDPPageAnalytics />;
    t18 = <PDPFooter />;
    $[34] = t17;
    $[35] = t18;
  } else {
    t17 = $[34];
    t18 = $[35];
  }
  let t19;
  if ($[36] !== pdpContextValue || $[37] !== t12 || $[38] !== t14 || $[39] !== t16) {
    t19 = <PDPContext.Provider value={pdpContextValue}>{t12}{t14}{t16}{t17}{t18}</PDPContext.Provider>;
    $[36] = pdpContextValue;
    $[37] = t12;
    $[38] = t14;
    $[39] = t16;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  return t19;
};
PDPContextRegion.propTypes = {
  slugOverride: PropTypes.string,
  isEditOrder: PropTypes.bool,
  pdpIsModularBYOProduct: PropTypes.bool,
  productOptionsPromise: PropTypes.object,
  productBasicDetailsPromise: PropTypes.object,
  productHeroImageFromServer: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  }),
  productCylindoHeroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  })
};
export default PDPContextRegion;